'use client'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  DownloadDelayedRefusal,
  delayUntilReadyMs,
  parseDownloadRefusal,
} from '@/lib/download-protection/refusals'
import { sendDownloadProtectionEvent } from '@/lib/analytics/download-protection'
import { useDownloadRefusalStore } from '@/lib/store/download-refusal-store'

/**
 * Shows the member what a download-protection refusal means, and resumes the download when
 * the refusal is only a wait.
 *
 * This is the one entry point the download hook calls. It takes the raw error body so the
 * hook does not have to recognise the codes, and answers whether it presented anything: a
 * `false` means the caller should fall through to its own error handling.
 *
 * `retry` re-runs the same download, optionally carrying a challenge token for the request
 * to send on. It is optional because not every download path can hand one over; without it
 * a delayed member is told when the wait is over instead of having the download resume by
 * itself, and a challenged one is told to start the download again once they have passed.
 */
export function presentDownloadRefusal(
  body: unknown,
  retry?: (challengeToken?: string) => void | Promise<void>
): boolean {
  const refusal = parseDownloadRefusal(body)

  if (!refusal) {
    return false
  }

  sendDownloadProtectionEvent('download_protection_refusal_presented', {
    refusal_code: refusal.code,
    can_retry: Boolean(retry),
  })

  if (refusal.code === 'DOWNLOAD_DELAYED') {
    showDelayedCountdown(refusal, retry)
    return true
  }

  useDownloadRefusalStore.getState().show(refusal, retry)

  return true
}

/** One toast id for every delayed download, so a second refusal replaces the first. */
const DELAYED_TOAST_ID = 'download-delayed'

/**
 * A toast that counts down to the moment the download is ready, then resumes it once.
 *
 * The wait is measured once, here, when the refusal arrives, so nothing polls the API while
 * the member waits and the countdown does not drift by however long the toast takes to
 * render. A refusal of the retry arrives here again under the same toast id, so the new wait
 * replaces the old countdown instead of stacking a second toast beside it.
 *
 * The toast stays until the wait is over, so it carries a close button: a member who would
 * rather not wait, or whose clock disagrees with the server's, can put it away.
 */
function showDelayedCountdown(
  refusal: DownloadDelayedRefusal,
  retry?: (challengeToken?: string) => void | Promise<void>
): void {
  const readyAt = Date.now() + delayUntilReadyMs(refusal)

  toast(
    <DelayedCountdown
      // Remount on a new wait so the countdown restarts rather than resuming the old one.
      key={readyAt}
      refusal={refusal}
      readyAt={readyAt}
      canRetry={Boolean(retry)}
      onReady={() => {
        toast.dismiss(DELAYED_TOAST_ID)
        sendDownloadProtectionEvent('download_protection_delay_elapsed', {
          refusal_code: 'DOWNLOAD_DELAYED',
          can_retry: Boolean(retry),
        })
        void retry?.()
      }}
    />,
    { id: DELAYED_TOAST_ID, duration: Number.POSITIVE_INFINITY, closeButton: true }
  )
}

function DelayedCountdown({
  refusal,
  readyAt,
  canRetry,
  onReady,
}: {
  refusal: DownloadDelayedRefusal
  readyAt: number
  canRetry: boolean
  onReady: () => void
}) {
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntil(readyAt))

  // `onReady` dismisses this toast, so keep it out of the effect's dependencies: a fresh
  // identity on re-render would restart the countdown.
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady

  useEffect(() => {
    // The toast lingers for its dismiss animation after the wait is over, so the countdown
    // has to stop itself: without this the interval would keep firing and retry the
    // download once a second.
    let hasFired = false
    let interval = 0

    const tick = () => {
      const remaining = secondsUntil(readyAt)

      if (remaining > 0) {
        setSecondsLeft(remaining)
        return
      }

      if (hasFired) {
        return
      }

      hasFired = true
      window.clearInterval(interval)
      setSecondsLeft(0)
      onReadyRef.current()
    }

    tick()
    interval = window.setInterval(tick, 1000)

    return () => window.clearInterval(interval)
  }, [readyAt])

  return (
    <div className="tw-flex tw-flex-col tw-gap-1" role="status" aria-live="polite">
      <strong>{refusal.message ?? 'Your download is queued'}</strong>
      <span>
        {secondsLeft > 0
          ? `Ready in ${formatCountdown(secondsLeft)}${canRetry ? ', starting automatically' : ''}`
          : canRetry
            ? 'Starting your download...'
            : 'Ready now. Start the download again.'}
      </span>
    </div>
  )
}

function secondsUntil(readyAt: number): number {
  return Math.max(Math.ceil((readyAt - Date.now()) / 1000), 0)
}

function formatCountdown(totalSeconds: number): string {
  if (totalSeconds < 60) {
    return `${totalSeconds}s`
  }

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}m ${String(seconds).padStart(2, '0')}s`
}
